import {
  RemoveJsxChange,
  WrapDivChange,
} from "@/store/zustand/changeLedgerTypes";
import { NavTreeNode } from "@/utils/types";
import { TempoElement } from "@/utils/tempoElement";
import {
  NavTreeUpdates,
  fixUniquePathsForAllChildren,
} from "./navTreeLookupUtils";
import { useCanvasStore } from "@/components/Canvas/stores/canvasStore";
import {
  getCodebaseIdFromNode,
  getDomUniquePathFromNode,
} from "@/components/Canvas/utils/utils";
import { getElementsForCodebaseId } from "./domUtils";
import { ApplyInstantUpdateProps } from "./utils";

export const applyRemoveJsx = (props: ApplyInstantUpdateProps) => {
  const { iframeDocument, storyboardId, persistentData } = props;
  const change = props.change as RemoveJsxChange;
  const parentToDomElementsRemoved: {
    [parentElementKey: string]: {
      elementKeyRemoved: string;
      htmlRemoved: string;
      index: number;
    }[];
  } = {};

  change.changeFields.codebaseIdsToRemove?.forEach(
    (codebaseIdToRemove: string) => {
      const elementsToRemove = getElementsForCodebaseId(
        iframeDocument,
        codebaseIdToRemove,
      );

      if (!elementsToRemove.length) {
        return;
      }

      persistentData.storyboardsAppliedTo.add(storyboardId);

      // For each item that is removed, save the inner HTML in case it gets deleted and we want to undo
      elementsToRemove.forEach((item) => {
        const domUniquePath = getDomUniquePathFromNode(item as HTMLElement);
        const parentDomUniquePath = getDomUniquePathFromNode(
          item.parentElement as HTMLElement,
        );
        const parentCodebaseId = getCodebaseIdFromNode(
          item.parentElement as HTMLElement,
        );

        const elementKeyRemoved = new TempoElement(
          codebaseIdToRemove,
          storyboardId,
          domUniquePath,
        ).getKey();
        const parentElementKey = parentCodebaseId
          ? new TempoElement(
              parentCodebaseId,
              storyboardId,
              parentDomUniquePath,
            ).getKey()
          : null;

        if (elementKeyRemoved && parentElementKey && item.parentElement) {
          if (!parentToDomElementsRemoved[parentElementKey]) {
            parentToDomElementsRemoved[parentElementKey] = [];
          }

          // Get the index of this element among its siblings
          const index = Array.from(item.parentElement.children).indexOf(
            item as Element,
          );

          parentToDomElementsRemoved[parentElementKey].push({
            htmlRemoved: item.outerHTML,
            elementKeyRemoved,
            index,
          });
        }

        item.remove();
      });
    },
  );

  // Important - only save if there were changes applies to this storyboard so that
  // irrelevant changes to other storyboards don't clear this state
  if (Object.keys(parentToDomElementsRemoved).length > 0) {
    persistentData.instantUpdateData["parentToDomElementsRemoved"] =
      parentToDomElementsRemoved;
  }
};

export const undoRemoveJsx = (props: ApplyInstantUpdateProps) => {
  const { iframeDocument, persistentData } = props;
  // Restore elements using saved instant update data
  const parentToDomElementsRemoved = persistentData.instantUpdateData?.[
    "parentToDomElementsRemoved"
  ] as
    | {
        [parentElementKey: string]: {
          elementKeyRemoved: string;
          htmlRemoved: string;
          index: number;
        }[];
      }
    | undefined;
  if (!parentToDomElementsRemoved) return;

  // Iterate through each parent and restore its removed children
  Object.entries(parentToDomElementsRemoved).forEach(
    ([parentElementKey, removedElements]) => {
      // Extract the parent's domUniquePath from the elementKey
      const parentElement = TempoElement.fromKey(parentElementKey);
      if (!parentElement) return;

      // Find the parent element in the DOM
      let parentNode;
      if (parentElement.codebaseId === "body") {
        parentNode = iframeDocument.getElementsByTagName("body")[0];
      } else if (parentElement.codebaseId === "root") {
        parentNode = iframeDocument.getElementById("root");
      } else if (parentElement.codebaseId === "__next") {
        parentNode = iframeDocument.getElementById("__next");
      } else {
        const parentNodes = getElementsForCodebaseId(
          iframeDocument,
          parentElement.codebaseId,
        );

        // Pick the one that matches the unique path
        parentNode = parentNodes.find((element) => {
          const domUniquePath = getDomUniquePathFromNode(
            element as HTMLElement,
          );
          return domUniquePath === parentElement.uniquePath;
        }) as HTMLElement;
      }

      if (!parentNode) {
        console.log("Could not find parent element for undo remove jsx");
        return;
      }

      // Restore each removed child element in reverse order to maintain correct indices
      const reversedElements = [...removedElements].reverse();
      reversedElements.forEach(({ htmlRemoved, elementKeyRemoved, index }) => {
        // Create a temporary container to parse the HTML
        const tempDiv = iframeDocument.createElement("div");
        tempDiv.innerHTML = htmlRemoved;

        // Get the element to insert
        const elementToInsert = tempDiv.firstChild;
        if (!elementToInsert) return;

        // Insert at the correct position
        const children = parentNode.children;
        if (index >= children.length) {
          // If the index is beyond current children, append at the end
          parentNode.appendChild(elementToInsert);
        } else {
          // Insert before the element at the original index
          parentNode.insertBefore(elementToInsert, children[index]);
        }
      });
    },
  );
};

// Update nav tree for REMOVE_JSX change
export const applyRemoveJsxToNavTree = (
  props: ApplyInstantUpdateProps,
): void => {
  const { navTreeUpdates, persistentData } = props;
  const change = props.change as RemoveJsxChange;
  const codebaseIdsToRemove = change.changeFields.codebaseIdsToRemove ?? [];
  if (codebaseIdsToRemove.length === 0) return;

  // Find nodes to remove using the lookup for efficiency
  const parentNodesToFix: NavTreeNode[] = [];
  const parentToNavTreeNodesRemoved: {
    [parentElementKey: string]: { node: NavTreeNode; index: number }[];
  } = {};

  for (const codebaseId of codebaseIdsToRemove) {
    const nodes = navTreeUpdates.codebaseIdLookup[codebaseId] || [];
    nodes.forEach((node: NavTreeNode) => {
      const parent = node.parent;
      if (!parent) return;

      parentNodesToFix.push(parent as NavTreeNode);

      // Get the index before removing
      const index = parent.children?.indexOf(node) ?? -1;

      parent.children =
        parent.children?.filter((child) => child !== node) || [];

      const parentElementKey = parent.tempoElement?.getKey();
      if (parentElementKey && index !== -1) {
        if (!parentToNavTreeNodesRemoved[parentElementKey]) {
          parentToNavTreeNodesRemoved[parentElementKey] = [];
        }

        parentToNavTreeNodesRemoved[parentElementKey].push({ node, index });
      }
    });
  }

  // Fix the unique paths of all children
  parentNodesToFix.forEach((parentNode) => {
    fixUniquePathsForAllChildren(parentNode as NavTreeNode);
  });

  // Important - only save if there were changes applies to this storyboard so that
  // irrelevant changes to other storyboards don't clear this state
  if (Object.keys(parentToNavTreeNodesRemoved).length > 0) {
    persistentData.instantUpdateData["parentToNavTreeNodesRemoved"] =
      parentToNavTreeNodesRemoved;
  }
};

// Undo nav tree changes for REMOVE_JSX change
export const undoRemoveJsxToNavTree = (
  props: ApplyInstantUpdateProps,
): void => {
  const { navTreeUpdates, persistentData } = props;
  const change = props.change as RemoveJsxChange;
  // Get the saved nav tree nodes that were removed
  const parentToNavTreeNodesRemoved = persistentData.instantUpdateData?.[
    "parentToNavTreeNodesRemoved"
  ] as
    | { [parentElementKey: string]: { node: NavTreeNode; index: number }[] }
    | undefined;
  if (!parentToNavTreeNodesRemoved) return;

  const parentNodesToFix = new Set<NavTreeNode>();

  // Restore each removed node back to its parent
  Object.entries(parentToNavTreeNodesRemoved).forEach(
    ([parentElementKey, removedNodesWithIndices]) => {
      // Find the parent node using the lookup
      const parentNode = navTreeUpdates.elementKeyLookup[parentElementKey];
      if (!parentNode) {
        console.log(
          "Could not find parent node for undo remove jsx in nav tree",
        );
        return;
      }

      // Restore each removed child node in reverse order to maintain correct indices
      const reversedNodes = [...removedNodesWithIndices].reverse();
      reversedNodes.forEach(({ node: removedNode, index }) => {
        // Re-establish parent-child relationship
        removedNode.parent = parentNode;
        if (!parentNode.children) {
          parentNode.children = [];
        }

        // Insert at the correct position
        if (index >= parentNode.children.length) {
          // If index is beyond current children, append at the end
          parentNode.children.push(removedNode);
        } else {
          // Insert at the original index
          parentNode.children.splice(index, 0, removedNode);
        }

        // Track parent for fixing unique paths later
        parentNodesToFix.add(parentNode);
      });
    },
  );

  // Fix the unique paths of all affected parent nodes and their children
  parentNodesToFix.forEach((parentNode) => {
    fixUniquePathsForAllChildren(parentNode);
  });
};

// Consolidated apply function that handles both DOM and nav tree
export const applyRemoveJsxChange = (props: ApplyInstantUpdateProps): void => {
  // Apply DOM changes
  applyRemoveJsx(props);

  // Apply nav tree changes
  if (!props.skipUpdateNavTree) {
    applyRemoveJsxToNavTree(props);
  }
};

// Consolidated undo function that handles both DOM and nav tree
export const undoRemoveJsxChange = (props: ApplyInstantUpdateProps): void => {
  // Undo DOM changes
  undoRemoveJsx(props);

  // Undo nav tree changes
  if (!props.skipUpdateNavTree) {
    undoRemoveJsxToNavTree(props);
  }
};
